import { DashboardIcon } from "@/assets/images/sidebar-icons/dashboard"
import { ExplorerIcon } from "@/assets/images/sidebar-icons/explorer"
import { PaymentIcon } from "@/assets/images/sidebar-icons/payment"
import { TrainingIcon } from "@/assets/images/sidebar-icons/training"
import { LogoutIcon } from "@/assets/images/svgIcons/logoutIcon"
import { MessagesIcon } from "@/assets/images/svgIcons/messagesIcon"
import { ProfileTypeAthleteIcon } from "@/assets/images/svgIcons/profileTypeAthleteIcon"
import { ProfileTypeCoachIcon } from "@/assets/images/svgIcons/profileTypeCoachIcon"
import { ProfileTypeGymIcon } from "@/assets/images/svgIcons/profileTypeGymIcon"
import { Theme, useTheme } from "@/components/theme/themeProvider"
import type { DropdownMenuItem, MenuItem } from "@/components/ui/layout/menuSidebar/type-sidebar"
import { createBoxMenuModel } from "@/modules/app/models/boxMenuItems"
import { createCoachMenuModel } from "@/modules/app/models/coachMenuItems"
import { useLogout } from "@/modules/auth/hooks/useLogout"
import { useBoxs, useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxStaffRole } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useChatUnreadCount } from "@/modules/chat/hooks/useChatUnreadCount"
import { useCurrentCoachIfExists } from "@/modules/coach/hooks/useProfessionalCoach"
import { Config } from "@/modules/config"
import { useCurrentBoxId, useCurrentCoachId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { FeatureKeys } from "@/modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@/modules/featureToggle/hooks/useFeatureToggle"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import { useUser } from "@/modules/users/hooks/useUser"
import { delay } from "@/modules/utils/utils"
import { word } from "@modules/core/utils/i18n"
import { RouteNavigate } from "@modules/navigation/hooks/useBaseNavigate"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { getMySpacePath, useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { CircleQuestionMarkIcon, Dot, EarthIcon, PlusIcon, UserIcon } from "lucide-react"
import { useNavigate } from "react-router"

export const useMenuModels = () => {
  const { result: box } = useCurrentBox()
  const coachId = useCurrentCoachId()
  const user = useUser()
  const { hasUnread: hasUnreadMessage } = useChatUnreadCount()

  const menu: MenuItem[] = []

  if (box) {
    const boxMenuItems = createBoxMenuModel({
      box,
      hasUnreadMessage,
    })
    menu.push(...boxMenuItems)
  } else if (coachId) {
    const coachMenuItems = createCoachMenuModel({
      coachId,
      hasUnreadMessage,
    })
    menu.push(...coachMenuItems)
  } else {
    const itemDashboard: MenuItem = {
      label: word("menu.dashboard"),
      icon: <DashboardIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
      to: getMySpacePath(RouteNavigate.Root),
      relativeTo: [getMySpacePath(RouteNavigate.Root)],
    }
    const itemExplorer: MenuItem = {
      label: word("menu.explorer"),
      icon: <ExplorerIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
      to: getMySpacePath(Path.MySpace.Explore),
      relativeTo: [getMySpacePath(Path.MySpace.Explore)],
    }
    const itemTraining: MenuItem = {
      label: word("menu.training"),
      icon: <TrainingIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
      to: getMySpacePath(Path.MySpace.Training),
      relativeTo: [
        getMySpacePath(Path.MySpace.Training),
        getMySpacePath(Path.MySpace.ProgramDetails(":id")),
        getMySpacePath(Path.MySpace.ProgramOnOffDetails(":id")),
        getMySpacePath(Path.MySpace.Templates),
        getMySpacePath(Path.MySpace.TemplateWeek(":id")),
      ],
    }
    const itemChat: MenuItem = {
      label: word("menu.chat"),
      icon: (
        <span className="relative inline-block">
          <MessagesIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />
          {hasUnreadMessage && (
            <span className="absolute -top-0.5 right-0 flex h-2 w-2 items-center justify-center">
              <Dot strokeWidth={20} color="currentColor" className="text-red-500" />
            </span>
          )}
        </span>
      ),
      to: getMySpacePath(Path.Chat.Root),
      badge: hasUnreadMessage ? ("new" as "new" | "updated") : undefined,
      relativeTo: [getMySpacePath(Path.Chat.Root)],
    }
    const itemFinances: MenuItem = {
      label: word("menu.transactions"),
      icon: <PaymentIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
      to: getMySpacePath(Path.MySpace.Finances.Root),
      relativeTo: [getMySpacePath(Path.MySpace.Finances.Root)],
    }
    // const itemTutorials = {
    //   label: word("menu.tutorials"),
    //   icon: "hup-tutorials",
    //   to: getMySpacePath(Path.MySpace.Tutorials),
    //   relativeTo: [getMySpacePath(Path.MySpace.Tutorials)],
    // }

    menu.push(itemDashboard)
    menu.push(itemExplorer)
    menu.push(itemTraining)
    menu.push(itemChat)
    if (user.stripeAccountId && user.stripeAccountStatus === StripeAccountStatus.OK) menu.push(itemFinances)
    // menu.push(itemTutorials)
  }

  return menu
}

export const useDropdownMenuModel = (openLanguagePopup?: () => void) => {
  const user = useUser()
  const boxId = useCurrentBoxId()
  const { data: boxs } = useBoxs()
  const language = useLanguage()
  const { setTheme } = useTheme()
  const logout = useLogout()
  const mySpaceNavigate = useMySpaceNavigate()
  const boxNavigate = useBoxNavigate()
  const navigate = useNavigate()
  const canCreateProfessionalCoach = useFeatureToggle(FeatureKeys.CoachSpaceAccess)
  const { currentCoach } = useCurrentCoachIfExists()
  const coachHasNotCompletedOnboarding = !currentCoach?.state.description

  const mySpaceOption: DropdownMenuItem = {
    icon: <ProfileAvatar profile={{ avatar: user.avatar ?? null, username: user.username }} className="size-8" />,
    title: user.firstname + " " + user.lastname,
    subTitle: word("menu.profile.mySpace.type"),
    profileTypeIcon: <ProfileTypeAthleteIcon className="size-4.5" />,
    selected: boxId === null,
    onPress: () => {
      setTheme(Theme.AthleteLight)
      void delay(100)
      mySpaceNavigate(RouteNavigate.Root)
    },
  }

  const boxOptions: DropdownMenuItem[] = boxs.map((box) => ({
    icon: <ProfileAvatar profile={{ avatar: box.logo, username: box.name }} className="size-8" />,
    title: box.name,
    subTitle: word("menu.profile.box.type") + (box.role === BoxStaffRole.READ_ONLY ? " - Read only" : ""),
    profileTypeIcon: <ProfileTypeGymIcon className="size-5" />,
    selected: boxId === box.id,
    onPress: () => {
      setTheme(Theme.OwnerDark)
      void delay(100)
      boxNavigate(RouteNavigate.Root, { newBoxId: box.id })
    },
  }))

  const addBoxOption: DropdownMenuItem = {
    icon: (
      <div className="flex size-8 items-center justify-center rounded-full bg-surface-background-float">
        <PlusIcon className="size-4.5" />
      </div>
    ),
    title: word("menu.profile.add_box.label"),
    onPress: () => {
      setTheme(Theme.OwnerDark)
      void delay(100)
      void navigate(Path.CreateBox)
    },
  }

  const coachesOptions: DropdownMenuItem[] = currentCoach
    ? [
        {
          icon: (
            <ProfileAvatar
              profile={{ avatar: currentCoach.state.profilePhoto, username: currentCoach.state.id }}
              className="size-8"
            />
          ),
          title: currentCoach.getCoachDisplayName(),
          profileTypeIcon: <ProfileTypeCoachIcon className="size-5.5" />,
          subTitle: word("menu.profile.coach.type"),
          onPress: () => {
            setTheme(Theme.CoachLight)
            void delay(100)
            if (coachHasNotCompletedOnboarding) {
              return void navigate(Path.CoachOnboarding)
            }
            return void navigate(Path.Coach.Root(currentCoach.state.id))
          },
        },
      ]
    : canCreateProfessionalCoach
      ? [
          {
            icon: (
              <div className="flex size-8 items-center justify-center rounded-full bg-surface-background-float">
                <PlusIcon className="size-4.5" />
              </div>
            ),
            title: word("menu.profile.add_coach.label"),
            onPress: () => {
              setTheme(Theme.CoachLight)
              void delay(100)
              void navigate(Path.CoachOnboarding)
            },
          },
        ]
      : []

  const settingOptions: DropdownMenuItem[] = [
    {
      icon: <UserIcon className="size-5" />,
      title: word("menu.profile.profile.label"),
      onPress: () => {
        if (boxId !== null) {
          boxNavigate(Path.Box.Profile)
        } else {
          mySpaceNavigate(Path.MySpace.Profile)
        }
      },
    },
    {
      icon: <EarthIcon className="size-5" />,
      title: word("menu.profile.language.label"),
      onPress: () => {
        openLanguagePopup?.()
      },
    },
    {
      icon: <CircleQuestionMarkIcon className="size-5" />,
      title: word("menu.profile.help_center.label"),
      onPress: () => {
        window.open(`${Config.HELP_CENTER_URL}/${language}`, "_blank")
      },
    },
  ]

  const logoutOption: DropdownMenuItem = {
    icon: <LogoutIcon color="currentColor" className="size-5 bg-transparent hover:bg-transparent" />,
    title: word("menu.profile.logout.label"),
    onPress: () => {
      void logout()
    },
  }

  return { mySpaceOption, boxOptions: [...boxOptions, addBoxOption], coachesOptions, settingOptions, logoutOption }
}
